<template>
    <div class="signin-page">
        <svg
            class="signin-bg"
            viewBox="0 0 1200 800"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            aria-hidden="true"
        >
            <defs>
                <path
                    id="sp1"
                    d="M -60 160 C 300 40 620 260 1260 130"
                />
                <path
                    id="sp2"
                    d="M -60 650 C 360 770 720 500 1260 690"
                />
                <path
                    id="sp3"
                    d="M 120 -60 C 240 300 430 520 340 860"
                />
                <path
                    id="sp4"
                    d="M 1080 -60 C 960 260 1060 560 840 860"
                />
                <!-- Logo geometry centred at the origin with its point facing +x,
                     so animateMotion rotate="auto" makes it lead along the path. -->
                <g
                    id="logoGeo"
                    transform="rotate(-45) translate(-66.47 -66.34)"
                >
                    <path
                        d="M66.4813 -2.52501e-05C29.846 -2.01924e-05 -0.000276055 29.5743 -0.000279427 66.4815C-0.000280101 103.117 29.846 132.691 66.4813 132.691C82.9729 132.691 132.953 132.691 132.953 132.691C132.953 132.691 132.953 66.7437 132.953 66.4718C132.953 29.5646 103.126 -0.00973454 66.4813 -2.52501e-05ZM66.4715 92.4525C52.0583 92.4525 40.3645 80.7588 40.3548 66.3358C40.3548 51.9226 52.0486 40.2288 66.4715 40.2191C80.8848 40.2191 92.5785 51.9129 92.5882 66.3358C92.598 80.7588 80.8945 92.4428 66.4715 92.4525Z"
                        fill="#03ff86"
                    />
                </g>
            </defs>
            <g class="signin-lines">
                <use xlink:href="#sp1" />
                <use xlink:href="#sp2" />
                <use xlink:href="#sp3" />
                <use xlink:href="#sp4" />
            </g>
            <g class="signin-bg-logo">
                <use
                    xlink:href="#logoGeo"
                    transform="scale(0.16)"
                />
                <!-- One logo, chained along each path in turn so it travels the
                     whole network. Each leg starts when the previous ends, and the
                     hand-offs happen off-screen at the path ends. -->
                <animateMotion
                    id="leg1"
                    dur="11s"
                    rotate="auto"
                    begin="0; leg4.end"
                ><mpath xlink:href="#sp1" /></animateMotion>
                <animateMotion
                    id="leg2"
                    dur="11s"
                    rotate="auto"
                    begin="leg1.end"
                ><mpath xlink:href="#sp2" /></animateMotion>
                <animateMotion
                    id="leg3"
                    dur="8s"
                    rotate="auto"
                    begin="leg2.end"
                ><mpath xlink:href="#sp3" /></animateMotion>
                <animateMotion
                    id="leg4"
                    dur="10s"
                    rotate="auto"
                    begin="leg3.end"
                ><mpath xlink:href="#sp4" /></animateMotion>
            </g>
        </svg>
        <div class="signin-card">
            <img
                class="signin-logo"
                src="/new_logo_black.svg"
                alt="LITTA"
            />
            <h1 class="signin-title">Beehive</h1>
            <p class="signin-subtitle">
                Sign in with your Litta account to continue.
            </p>

            <button
                class="signin-button"
                type="button"
                :disabled="loading"
                @click="$emit('sign-in')"
            >
                <span
                    v-if="loading"
                    class="signin-spinner"
                    aria-hidden="true"
                ></span>
                <i
                    v-else
                    class="pi pi-google signin-google"
                    aria-hidden="true"
                ></i>
                <span>{{ loading ? 'Signing in...' : 'Sign in with Google' }}</span>
            </button>

            <p
                v-if="error"
                class="signin-error"
                role="alert"
            >
                {{ error }}
            </p>
        </div>
    </div>
</template>

<script lang="ts">
export default {
    name: 'SignInPage',
    props: {
        loading: { type: Boolean, default: false },
        error: { type: String as () => string | null, default: null },
    },
    emits: ['sign-in'],
}
</script>

<style scoped>
.signin-page {
    position: relative;
    overflow: hidden;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #f7f7f0;
    font-family: 'DM Sans', sans-serif;
}

.signin-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
}

.signin-lines use {
    fill: none;
    stroke: #003732;
    stroke-width: 1.5;
    stroke-dasharray: 1.5 10;
    stroke-linecap: round;
    opacity: 0.12;
}

.signin-bg-logo {
    opacity: 0.55;
}

.signin-card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 400px;
    background: #ffffff;
    border: 1px solid #eeede6;
    border-radius: 16px;
    padding: 40px 32px;
    text-align: center;
    box-shadow: 0 8px 30px rgba(0, 55, 50, 0.08);
}

.signin-logo {
    height: 40px;
    width: auto;
    margin: 0 auto 24px;
    display: block;
}

.signin-title {
    margin: 0 0 8px;
    font-size: 26px;
    font-weight: 700;
    color: #003732;
}

.signin-subtitle {
    margin: 0 0 28px;
    font-size: 14px;
    color: #5e5c53;
}

.signin-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 14px 20px;
    border: none;
    border-radius: 9999px;
    background: #03ff86;
    color: #003732;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.signin-button:hover:not(:disabled) {
    background: #00e676;
}

.signin-button:disabled {
    opacity: 0.7;
    cursor: default;
}

.signin-google {
    font-size: 18px;
    line-height: 1;
}

.signin-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(0, 55, 50, 0.3);
    border-top-color: #003732;
    border-radius: 50%;
    animation: signin-spin 0.7s linear infinite;
}

@keyframes signin-spin {
    to {
        transform: rotate(360deg);
    }
}

.signin-error {
    margin: 16px 0 0;
    font-size: 13px;
    color: #b42318;
}

@media (prefers-reduced-motion: reduce) {
    .signin-bg-logo {
        display: none;
    }
}
</style>
